import { FC, MouseEvent, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, PreloaderCircular } from '@shared/ui/kit';
import { palette } from '@shared/tokens/palette.stylex';
import { StylesSettings } from '@shared/types/types';
import { useBodyScrollLock } from '@shared/hooks/useBodyScrollLock';
import check from '../../assets/check.webp';
import cross from '../../assets/cross.webp';
import styles from './change-decision.module.scss';

/** accept — участник соглашается после отказа, decline — отказывается после согласия. */
export type ChangeDecisionVariant = 'accept' | 'decline';

/** modal — карточка поверх затемнения на десктопе, screen — полноэкранное состояние на мобильной. */
export type ChangeDecisionLayout = 'modal' | 'screen';

interface ChangeDecisionProps {
  variant: ChangeDecisionVariant;
  layout: ChangeDecisionLayout;
  isPending: boolean;
  hasFailed: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const DECISION_CONTENT: Record<ChangeDecisionVariant, { illustration: string; text: string; confirmLabel: string }> = {
  accept: {
    illustration: check,
    text: 'Мы рады, что ты сможешь разделить с нами это событие. Пожалуйста, подтверди свое решение',
    confirmLabel: 'Я пойду на мероприятие',
  },
  decline: {
    illustration: cross,
    text: 'Очень жаль, что ты не сможешь разделить с нами это событие. Пожалуйста, подтверди свое решение',
    confirmLabel: 'Не пойду',
  },
};

const TITLE = 'Изменились планы?';

/** Формулировка как в `ErrorInvite`: другого текста об ошибке на этой странице нет. */
const ERROR_TEXT = 'Непредвиденная ошибка';

// Через `settings`, а не CSS-модуль: стили кита на stylex специфичнее селектора модуля и перебивают
// его. Размер `small` даёт подпись 12px и отступ 8px, а в макете 14/16px и боковые 20px.
const BUTTON_SIZES: Record<ChangeDecisionLayout, StylesSettings> = {
  modal: { fontSize: '14px', lineHeight: '20px', padding: '8px 20px' },
  screen: { fontSize: '16px', lineHeight: '20px', padding: '10px 20px', height: '40px' },
};

// Цвета фиксированы палитрой, как на странице билета: она светлая независимо от темы, а варианты
// кита в prefers-color-scheme: dark делают подпись «Отмены» белой на светлом фоне.
const CANCEL_COLORS: StylesSettings = {
  color: palette['grey-950'],
  background: 'transparent',
  border: `1px solid ${palette['darked-40']}`,
};

const CONFIRM_COLORS: StylesSettings = {
  color: palette.greyWhite,
  background: palette['bronze-500'],
  border: '1px solid transparent',
};

export const ChangeDecision: FC<ChangeDecisionProps> = ({
  variant,
  layout,
  isPending,
  hasFailed,
  onConfirm,
  onCancel,
}) => {
  const isModal = layout === 'modal';
  const { illustration, text, confirmLabel } = DECISION_CONTENT[variant];

  // В макете десктопной модалки отказа обе кнопки фиксированы на 132px. В модалке принятия и на
  // мобильной ширина оставлена по содержимому — «Я пойду на мероприятие» в 132px не укладывается.
  const buttonSize =
    isModal && variant === 'decline' ? { ...BUTTON_SIZES.modal, width: '132px' } : BUTTON_SIZES[layout];

  useBodyScrollLock(isModal);

  useEffect(() => {
    // Пока запрос в полёте, закрывать нечего: ответ всё равно сменит экран статуса.
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isPending) {
        onCancel();
      }
    };

    document.addEventListener('keydown', closeOnEscape);

    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onCancel, isPending]);

  // Затемнение закрывает окно только при клике по самому фону, а не по карточке внутри.
  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && !isPending) {
      onCancel();
    }
  };

  const content = (
    <>
      <img src={illustration} alt="" className={styles.illustration} />
      <p className={styles.title}>{TITLE}</p>
      <p className={styles.text}>{text}</p>
      {hasFailed && <p className={styles.error}>{ERROR_TEXT}</p>}

      <div className={styles.actions}>
        {isPending ? (
          <PreloaderCircular size="large" />
        ) : (
          <>
            <Button variant="secondary" size="small" onClick={onCancel} settings={{ ...buttonSize, ...CANCEL_COLORS }}>
              Отмена
            </Button>
            <Button variant="accent" size="small" onClick={onConfirm} settings={{ ...buttonSize, ...CONFIRM_COLORS }}>
              {confirmLabel}
            </Button>
          </>
        )}
      </div>
    </>
  );

  if (!isModal) {
    return <div className={styles.screen}>{content}</div>;
  }

  const modalRoot = document.getElementById('modal');

  if (!modalRoot) {
    return null;
  }

  return createPortal(
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.card}>{content}</div>
    </div>,
    modalRoot
  );
};
